import { Plus, Trash } from "@phosphor-icons/react";
import { useState } from "react";
import { toast } from "sonner";
import { API, fmtErr } from "../lib/api";
import { DOCS } from "../pages/Legal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";

const LEGAL_DOCUMENTS = [
  ["terms", "Terms & Conditions"],
  ["privacy", "Privacy Policy"],
  ["refund", "Refund Policy"],
  ["confidentiality", "Confidentiality Policy"],
  ["disclaimer", "Disclaimer Policy"],
];

export default function AdminSiteContent({ content, onSaved }) {
  const [saving, setSaving] = useState(false);

  if (!content) {
    return <p className="p-6 text-sm text-muted-foreground">Loading public page content…</p>;
  }

  const updateHomepage = (key, value) => {
    onSaved({
      ...content,
      homepage: { ...content.homepage, [key]: value },
    }, false);
  };

  const updateCard = (group, index, key, value) => {
    const cards = content.homepage[group].map((card, cardIndex) =>
      cardIndex === index ? { ...card, [key]: value } : card
    );
    updateHomepage(group, cards);
  };

  const updateLegal = (documentKey, key, value) => {
    onSaved({
      ...content,
      legal: {
        ...content.legal,
        [documentKey]: { ...content.legal[documentKey], [key]: value },
      },
    }, false);
  };

  const updateAnnouncement = (key, value) => {
    onSaved({
      ...content,
      announcement: { ...content.announcement, [key]: value },
    }, false);
  };

  const updateAnnouncementItem = (index, key, value) => {
    const updates = content.announcement.updates.map((item, itemIndex) =>
      itemIndex === index ? { ...item, [key]: value } : item
    );
    onSaved({
      ...content,
      announcement: { ...content.announcement, updates },
    }, false);
  };

  const addAnnouncement = () => {
    const updates = [
      ...content.announcement.updates,
      { title: "", message: "", link_url: "", link_label: "" },
    ];
    onSaved({
      ...content,
      announcement: { ...content.announcement, updates },
    }, false);
  };

  const removeAnnouncement = (index) => {
    const updates = content.announcement.updates.filter((_, itemIndex) => itemIndex !== index);
    onSaved({
      ...content,
      announcement: { ...content.announcement, updates },
    }, false);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await API.put("/admin/site-content", content);
      onSaved(data, true);
      toast.success("Public page content saved");
    } catch (error) {
      toast.error(fmtErr(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-6 space-y-5" data-testid="admin-site-content">
      <div className="rounded-lg border bg-card p-4">
        <p className="text-sm font-semibold">Public website content</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Update the landing-page copy and legal documents. Text is displayed as plain text; headings in legal content start with ##.
        </p>
      </div>
      <Tabs defaultValue="homepage">
        <TabsList className="h-auto flex-wrap justify-start" data-testid="admin-site-content-tabs">
          <TabsTrigger value="homepage" data-testid="admin-site-homepage-tab">Homepage</TabsTrigger>
          <TabsTrigger value="legal" data-testid="admin-site-legal-tab">Legal Policies</TabsTrigger>
          <TabsTrigger value="updates" data-testid="admin-site-updates-tab">Show Update</TabsTrigger>
        </TabsList>

        <TabsContent value="homepage" className="space-y-6">
          <section className="space-y-4 rounded-lg border bg-card p-5">
            <div>
              <h2 className="font-heading text-lg font-semibold">Hero section</h2>
              <p className="text-xs text-muted-foreground">The headline is split into the main phrase and its highlighted ending.</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {[
                ["hero_badge", "Intro badge"],
                ["hero_heading", "Headline"],
                ["hero_highlight", "Highlighted headline"],
                ["hero_primary_cta", "Primary button"],
                ["hero_secondary_cta", "Secondary button"],
              ].map(([key, label]) => (
                <div key={key} className="space-y-2">
                  <Label htmlFor={`site-${key}`}>{label}</Label>
                  <Input
                    id={`site-${key}`}
                    value={content.homepage[key]}
                    maxLength={key === "hero_heading" ? 160 : 100}
                    onChange={(event) => updateHomepage(key, event.target.value)}
                    data-testid={`site-content-${key}`}
                  />
                </div>
              ))}
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="site-hero-description">Description</Label>
                <Textarea
                  id="site-hero-description"
                  value={content.homepage.hero_description}
                  maxLength={500}
                  onChange={(event) => updateHomepage("hero_description", event.target.value)}
                  data-testid="site-content-hero-description"
                />
              </div>
            </div>
          </section>

          <section className="space-y-4 rounded-lg border bg-card p-5">
            <div>
              <h2 className="font-heading text-lg font-semibold">Service cards</h2>
              <p className="text-xs text-muted-foreground">Edit the service titles and descriptions shown on the public Comfile website.</p>
            </div>
            <div className="grid gap-4 lg:grid-cols-2">
              {content.homepage.features.map((card, index) => (
                <div key={`feature-${index}`} className="space-y-3 rounded-md border p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Feature {index + 1}</p>
                  <div className="space-y-2">
                    <Label htmlFor={`site-feature-title-${index}`}>Title</Label>
                    <Input
                      id={`site-feature-title-${index}`}
                      value={card.title}
                      maxLength={100}
                      onChange={(event) => updateCard("features", index, "title", event.target.value)}
                      data-testid={`site-content-feature-title-${index}`}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`site-feature-description-${index}`}>Description</Label>
                    <Textarea
                      id={`site-feature-description-${index}`}
                      value={card.description}
                      maxLength={300}
                      onChange={(event) => updateCard("features", index, "description", event.target.value)}
                      data-testid={`site-content-feature-description-${index}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="space-y-4 rounded-lg border bg-card p-5">
            <div>
              <h2 className="font-heading text-lg font-semibold">Why choose us cards</h2>
              <p className="text-xs text-muted-foreground">Edit the four benefits displayed below the feature grid.</p>
            </div>
            <div className="grid gap-4 lg:grid-cols-2">
              {content.homepage.benefits.map((card, index) => (
                <div key={`benefit-${index}`} className="space-y-3 rounded-md border p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Benefit {index + 1}</p>
                  <div className="space-y-2">
                    <Label htmlFor={`site-benefit-title-${index}`}>Title</Label>
                    <Input
                      id={`site-benefit-title-${index}`}
                      value={card.title}
                      maxLength={100}
                      onChange={(event) => updateCard("benefits", index, "title", event.target.value)}
                      data-testid={`site-content-benefit-title-${index}`}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`site-benefit-description-${index}`}>Description</Label>
                    <Textarea
                      id={`site-benefit-description-${index}`}
                      value={card.description}
                      maxLength={300}
                      onChange={(event) => updateCard("benefits", index, "description", event.target.value)}
                      data-testid={`site-content-benefit-description-${index}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="space-y-4 rounded-lg border bg-card p-5">
            <h2 className="font-heading text-lg font-semibold">Call to action</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              {[
                ["cta_heading", "Heading", 120],
                ["cta_button", "Button", 80],
              ].map(([key, label, maxLength]) => (
                <div key={key} className="space-y-2">
                  <Label htmlFor={`site-${key}`}>{label}</Label>
                  <Input
                    id={`site-${key}`}
                    value={content.homepage[key]}
                    maxLength={maxLength}
                    onChange={(event) => updateHomepage(key, event.target.value)}
                    data-testid={`site-content-${key}`}
                  />
                </div>
              ))}
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="site-cta-description">Description</Label>
                <Textarea
                  id="site-cta-description"
                  value={content.homepage.cta_description}
                  maxLength={300}
                  onChange={(event) => updateHomepage("cta_description", event.target.value)}
                  data-testid="site-content-cta-description"
                />
              </div>
            </div>
          </section>
          <Button onClick={save} disabled={saving} data-testid="site-content-save-homepage">
            {saving ? "Saving…" : "Save Homepage"}
          </Button>
        </TabsContent>

        <TabsContent value="legal" className="space-y-4">
          <Tabs defaultValue="terms">
            <TabsList className="h-auto flex-wrap justify-start" data-testid="admin-legal-doc-tabs">
              {LEGAL_DOCUMENTS.map(([key, label]) => (
                <TabsTrigger key={key} value={key} data-testid={`admin-legal-${key}-tab`}>{label}</TabsTrigger>
              ))}
            </TabsList>
            {LEGAL_DOCUMENTS.map(([key, label]) => (
              <TabsContent key={key} value={key} className="space-y-4 rounded-lg border bg-card p-5">
                <div>
                  <h2 className="font-heading text-lg font-semibold">{label}</h2>
                  <p className="text-xs text-muted-foreground">
                    Use lines starting with ## for section headings. Separate paragraphs with a blank line.
                  </p>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor={`legal-title-${key}`}>Page title</Label>
                    <Input
                      id={`legal-title-${key}`}
                      value={content.legal[key].title}
                      maxLength={120}
                      onChange={(event) => updateLegal(key, "title", event.target.value)}
                      data-testid={`site-content-legal-title-${key}`}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`legal-updated-${key}`}>Last updated label</Label>
                    <Input
                      id={`legal-updated-${key}`}
                      value={content.legal[key].updated}
                      maxLength={80}
                      onChange={(event) => updateLegal(key, "updated", event.target.value)}
                      data-testid={`site-content-legal-updated-${key}`}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`legal-content-${key}`}>Policy content</Label>
                  <Textarea
                    id={`legal-content-${key}`}
                    className="min-h-[420px] font-mono text-xs leading-5"
                    value={content.legal[key].content}
                    maxLength={50000}
                    onChange={(event) => updateLegal(key, "content", event.target.value)}
                    data-testid={`site-content-legal-body-${key}`}
                  />
                </div>
                <Button onClick={save} disabled={saving} data-testid={`site-content-save-${key}`}>
                  {saving ? "Saving…" : `Save ${label}`}
                </Button>
              </TabsContent>
            ))}
          </Tabs>
        </TabsContent>

        <TabsContent value="updates" className="space-y-4">
          <section className="space-y-5 rounded-lg border bg-card p-5" data-testid="admin-site-update-editor">
            <div>
              <h2 className="font-heading text-lg font-semibold">Login screen update</h2>
              <p className="text-sm text-muted-foreground">
                When enabled, this message appears over the image panel on the sign-in screen.
              </p>
            </div>
            <div className="flex items-center justify-between rounded-md border p-4">
              <div>
                <Label htmlFor="site-announcement-active">Show update to visitors</Label>
                <p className="mt-1 text-xs text-muted-foreground">Turn this off to hide all updates and show the standard sign-in artwork text.</p>
              </div>
              <Switch
                id="site-announcement-active"
                checked={!!content.announcement.active}
                onCheckedChange={(value) => updateAnnouncement("active", value)}
                data-testid="site-content-announcement-active"
              />
            </div>
            {content.announcement.updates.map((update, index) => (
              <section key={`announcement-${index}`} className="space-y-4 rounded-md border p-4" data-testid={`site-announcement-item-${index}`}>
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-medium">Update {index + 1}</h3>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removeAnnouncement(index)}
                    aria-label={`Remove update ${index + 1}`}
                    data-testid={`site-content-remove-announcement-${index}`}
                  >
                    <Trash size={16} className="mr-2" />
                    Remove
                  </Button>
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`site-announcement-title-${index}`}>Update title</Label>
                  <Input
                    id={`site-announcement-title-${index}`}
                    value={update.title}
                    maxLength={120}
                    onChange={(event) => updateAnnouncementItem(index, "title", event.target.value)}
                    data-testid={`site-content-announcement-title-${index}`}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`site-announcement-message-${index}`}>Update message</Label>
                  <Textarea
                    id={`site-announcement-message-${index}`}
                    value={update.message}
                    maxLength={2000}
                    onChange={(event) => updateAnnouncementItem(index, "message", event.target.value)}
                    data-testid={`site-content-announcement-message-${index}`}
                  />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor={`site-announcement-link-${index}`}>Link URL (optional)</Label>
                    <Input
                      id={`site-announcement-link-${index}`}
                      type="url"
                      placeholder="https://example.com/update.pdf"
                      value={update.link_url}
                      maxLength={2048}
                      onChange={(event) => updateAnnouncementItem(index, "link_url", event.target.value)}
                      data-testid={`site-content-announcement-link-${index}`}
                    />
                    <p className="text-xs text-muted-foreground">Use a public http(s) URL. For a PDF, paste its hosted file URL.</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`site-announcement-link-label-${index}`}>Link text</Label>
                    <Input
                      id={`site-announcement-link-label-${index}`}
                      placeholder="Read more or Download PDF"
                      value={update.link_label}
                      maxLength={80}
                      onChange={(event) => updateAnnouncementItem(index, "link_label", event.target.value)}
                      data-testid={`site-content-announcement-link-label-${index}`}
                    />
                  </div>
                </div>
              </section>
            ))}
            <Button
              type="button"
              variant="outline"
              onClick={addAnnouncement}
              disabled={content.announcement.updates.length >= 10}
              data-testid="site-content-add-announcement"
            >
              <Plus size={16} className="mr-2" />
              Add update
            </Button>
            <Button onClick={save} disabled={saving} data-testid="site-content-save-announcement">
              {saving ? "Saving…" : "Save Updates"}
            </Button>
          </section>
        </TabsContent>
      </Tabs>
    </div>
  );
}
